// Polling hooks for useQuery — one shared interval per query key plus the
// document-visibility gate for refetchIntervalInBackground. Split out of
// index.tsx when the frozen-mount fixes pushed the file over the 600-line
// lint budget (same split pattern as polling.ts / cache-entry.ts).
import { useEffect, useRef, useState } from "react";
import { registerPollingInterval } from "./polling";
import type { QueryClient, QueryFunctionContext } from "./index";

interface QueryPollingOpts {
  enabled: boolean;
  qc: QueryClient;
  k: string;
  refetchInterval: number | false | undefined;
  queryFn: (context: QueryFunctionContext) => Promise<unknown>;
}

/** Registers one shared polling interval per query key. */
export function useQueryPolling({
  enabled,
  qc,
  k,
  refetchInterval,
  queryFn,
}: QueryPollingOpts): void {
  const queryFnRef = useRef(queryFn);
  queryFnRef.current = queryFn;
  useEffect(() => {
    if (!enabled) return;
    if (!refetchInterval) return;
    const entry = qc._ensureEntry(k);
    return registerPollingInterval({
      qc,
      cacheKey: k,
      entry,
      interval: refetchInterval,
      queryFnRef,
    });
  }, [enabled, qc, k, refetchInterval]);
}

export function usePollingDocumentVisible(
  refetchIntervalInBackground: boolean | undefined,
): boolean {
  const [isVisible, setIsVisible] = useState(readDocumentVisible);
  useEffect(() => {
    if (refetchIntervalInBackground !== false) return;
    const updateVisibility = () => setIsVisible(readDocumentVisible());
    updateVisibility();
    document.addEventListener("visibilitychange", updateVisibility);
    return () =>
      document.removeEventListener("visibilitychange", updateVisibility);
  }, [refetchIntervalInBackground]);
  return refetchIntervalInBackground === false ? isVisible : true;
}

function readDocumentVisible(): boolean {
  return (
    typeof document === "undefined" || document.visibilityState !== "hidden"
  );
}
